import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Instagram, Facebook } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const { settings, loading } = useSettings();

    const handleLogoClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Fallback to mock data if settings are loading
    const contactInfo = loading ? { phone: '', phone2: '', email: '', facebook: '', instagram: '' } : {
        phone: settings.phone || '',
        phone2: settings.phone2 || '',
        email: settings.email || '',
        facebook: settings.facebook || '',
        instagram: settings.instagram || ''
    };

    return (
        <header className="bg-white shadow-md sticky top-0 z-50">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between py-4">
                    {/* Empty div for mobile to help with centering */}
                    <div className="w-10 md:hidden"></div>
                    {/* Logo */}
                    <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2" aria-label="Ankara Merkez Far Ana Sayfa">
                        <img src="/logo.jpg" alt="Ankara Merkez Far Logo" className="h-12" />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-8" aria-label="Ana menü">
                        <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="Ana Sayfa">
                            Ana Sayfa
                        </Link>
                        <Link to="/hizmetler" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="Hizmetlerimiz Sayfası">
                            Hizmetlerimiz
                        </Link>
                        <Link to="/hakkimizda" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="Hakkımızda Sayfası">
                            Hakkımızda
                        </Link>
                        <Link to="/iletisim" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="İletişim Sayfası">
                            İletişim
                        </Link>
                        <Link to="/galeri" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="Galeri Sayfası">
                            Galeri
                        </Link>
                        <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-colors font-medium" aria-label="Blog Sayfası">
                            Blog
                        </Link>
                    </nav>

                    {/* Phone Button - Both numbers with same style */}
                    <div className="hidden md:flex items-center space-x-2" role="group" aria-label="Telefon numaraları">
                        <a
                            href={`tel:${contactInfo.phone}`}
                            className="flex items-center space-x-2 bg-blue-700 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-all hover:scale-105 shadow-md"
                            aria-label={`Bizi arayın: ${contactInfo.phone}`}
                        >
                            <Phone size={20} aria-hidden="true" />
                            <span className="font-semibold">{contactInfo.phone}</span>
                        </a>
                        <a
                            href={`tel:${contactInfo.phone2}`}
                            className="flex items-center space-x-2 bg-blue-700 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-all hover:scale-105 shadow-md"
                            aria-label={`Alternatif telefon: ${contactInfo.phone2}`}
                        >
                            <Phone size={20} aria-hidden="true" />
                            <span className="font-semibold">{contactInfo.phone2}</span>
                        </a>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
                        aria-label={isMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
                    </button>
                </div>

                {/* Desktop Contact Info Bar */}
                <div className="hidden md:flex items-center justify-center py-2 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center space-x-6">
                        <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
                            <Phone size={16} />
                            <span className="font-medium">{contactInfo.phone}</span>
                        </a>
                        <a href={`tel:${contactInfo.phone2}`} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
                            <Phone size={16} />
                            <span className="font-medium">{contactInfo.phone2}</span>
                        </a>
                        <a href={`mailto:${contactInfo.email}`} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
                            <Mail size={16} />
                            <span className="font-medium">{contactInfo.email}</span>
                        </a>
                        {/* Social Media Icons */}
                        <div className="flex items-center space-x-3 ml-4">
                            <a
                                href={contactInfo.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 hover:text-blue-600 transition-colors"
                                aria-label="Facebook sayfamızı ziyaret edin"
                            >
                                <Facebook size={20} aria-hidden="true" />
                            </a>
                            <a
                                href={contactInfo.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 hover:text-pink-600 transition-colors"
                                aria-label="Instagram sayfamızı ziyaret edin"
                            >
                                <Instagram size={20} aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <nav className="md:hidden py-4 border-t border-gray-200">
                        <div className="flex flex-col space-y-4">
                            <Link
                                to="/"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Ana Sayfa
                            </Link>
                            <Link
                                to="/hizmetler"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Hizmetlerimiz
                            </Link>
                            <Link
                                to="/hakkimizda"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Hakkımızda
                            </Link>
                            <Link
                                to="/iletisim"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                İletişim
                            </Link>
                            <Link
                                to="/galeri"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Galeri
                            </Link>
                            <Link
                                to="/blog"
                                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Blog
                            </Link>
                            {/* Mobile phone numbers - same style */}
                            <div className="flex flex-row space-x-2 pt-2">
                                <a
                                    href={`tel:${contactInfo.phone}`}
                                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex-1 justify-center"
                                >
                                    <Phone size={18} />
                                    <span className="font-semibold text-sm">{contactInfo.phone}</span>
                                </a>
                                <a
                                    href={`tel:${contactInfo.phone2}`}
                                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex-1 justify-center"
                                >
                                    <Phone size={18} />
                                    <span className="font-semibold text-sm">{contactInfo.phone2}</span>
                                </a>
                            </div>
                            {/* Social Media Icons - Mobile */}
                            <div className="flex items-center space-x-4 pt-2">
                                <a
                                    href={contactInfo.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                                >
                                    <Facebook size={20} />
                                    <span>Facebook</span>
                                </a>
                                <a
                                    href={contactInfo.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 text-gray-700 hover:text-pink-600 transition-colors"
                                >
                                    <Instagram size={20} />
                                    <span>Instagram</span>
                                </a>
                            </div>
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};

export default Header;
